import { useEffect, useState } from 'react';
import { fetchResolvedUserPlan } from '../utils/planApi';
import { getUserIdentityCacheKey, resolveUserIdentity } from '../utils/userIdentity';

const CACHE_TTL_MS = 60 * 1000;
const planCache = new Map();

function getCachedPlan(cacheKey) {
  const cached = planCache.get(cacheKey);
  if (!cached) return null;
  if (Date.now() - cached.updatedAt > CACHE_TTL_MS) {
    planCache.delete(cacheKey);
    return null;
  }
  return cached.plan;
}

export default function useUserPlan(user) {
  const identity = resolveUserIdentity(user);
  const { email, userId } = identity;
  const cacheKey = getUserIdentityCacheKey(identity);

  const [plan, setPlan] = useState(() => {
    if (!cacheKey) return 'FREE';
    return getCachedPlan(cacheKey) || 'FREE';
  });
  const [isLoading, setIsLoading] = useState(() => Boolean(cacheKey && !getCachedPlan(cacheKey)));

  useEffect(() => {
    if (!cacheKey) {
      setPlan('FREE');
      setIsLoading(false);
      return;
    }

    const cached = getCachedPlan(cacheKey);
    if (cached) {
      setPlan(cached);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    fetchResolvedUserPlan({ email, userId })
      .then((nextPlan) => {
        if (cancelled) return;
        planCache.set(cacheKey, { plan: nextPlan, updatedAt: Date.now() });
        setPlan(nextPlan);
      })
      .catch(() => {
        if (cancelled) return;
        setPlan('FREE');
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [cacheKey, email, userId]);

  return {
    plan,
    isPro: plan === 'PRO',
    isLoading,
  };
}
